import React, {useEffect, useState} from "react";
import { styled } from '@mui/material/styles';
import {Container} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {Search as SearchIcon} from "@mui/icons-material";
import InputBase from "@mui/material/InputBase";
import {useTranslation} from "react-i18next";
import {InputAdornment, IconButton, CircularProgress} from '@mui/material';

const PREFIX = 'SearchForm';

const classes = {
  search: `${PREFIX}-search`,
  searchIcon: `${PREFIX}-searchIcon`,
  inputRoot: `${PREFIX}-inputRoot`,
  inputInput: `${PREFIX}-inputInput`
};

const StyledContainer = styled(Container)((
  {
    theme
  }
) => ({
  [`& .${classes.search}`]: {
    position: 'relative',
    left: '-5px',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
  },

  [`& .${classes.searchIcon}`]: {
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${classes.inputRoot}`]: {
    color: 'inherit',
    width: '100%',
  },

  [`& .${classes.inputInput}`]: {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
  }
}));

function useDebounce(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}

export default function SearchForm(props) {

  const {t} = useTranslation();
  const [search, setSearch] = useState(props.searchValue);
  const debouncedSearch = useDebounce(search, 500);
  const searchRef = React.useRef(null);

  useEffect(() => {
    if (search && search.length > 0) {
      props.search(search);
    } else {
      props.search("");
    }
  }, [debouncedSearch]);

  return (
    <StyledContainer disableGutters>
      <div className={classes.search}>
        <div className={classes.searchIcon}>
          <SearchIcon/>
        </div>
        <InputBase
          onFocus={props.onFocusInput ? props.onFocusInput : undefined}
          placeholder={props.searchText || t('topMenu.searchBar')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
          }}
          inputProps={{'aria-label': 'search'}}
          ref={searchRef}
          endAdornment={
            <InputAdornment position="end">
              <IconButton aria-label="toggle password visibility" size="large">
                {props.loading && <CircularProgress color="primary" size={28} />}
              </IconButton>
            </InputAdornment>
          }
        />
      </div>
    </StyledContainer>
  );
}
